/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Comment } from '../models/comment';
import { CommentBundle } from '../models/comment-bundle';
import { CreateCommentRequest } from '../models/create-comment-request';
import { UpdateCommentRequest } from '../models/update-comment-request';


/**
 * Comments attached to clients, end customers, or invoices
 */
@Injectable({
  providedIn: 'root',
})
export class CommentsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getInvoiceCommentBundle
   */
  static readonly GetInvoiceCommentBundlePath = '/invoices/{id}/comments';

  /**
   * List comments visible on the invoice page.
   *
   * Returns comments grouped by parent type — the invoice's own comments, its client's comments, and its end customer's comments (empty when the invoice has no end customer). Each list is sorted by created_at descending.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getInvoiceCommentBundle()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInvoiceCommentBundle$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<CommentBundle>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.GetInvoiceCommentBundlePath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CommentBundle>;
      })
    );
  }

  /**
   * List comments visible on the invoice page.
   *
   * Returns comments grouped by parent type — the invoice's own comments, its client's comments, and its end customer's comments (empty when the invoice has no end customer). Each list is sorted by created_at descending.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getInvoiceCommentBundle$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInvoiceCommentBundle(params: {
    id: number;
    context?: HttpContext
  }
): Observable<CommentBundle> {

    return this.getInvoiceCommentBundle$Response(params).pipe(
      map((r: StrictHttpResponse<CommentBundle>) => r.body as CommentBundle)
    );
  }

  /**
   * Path part for operation createInvoiceComment
   */
  static readonly CreateInvoiceCommentPath = '/invoices/{id}/comments';

  /**
   * Add a comment to an invoice.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createInvoiceComment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createInvoiceComment$Response(params: {
    id: number;
    context?: HttpContext
    body: CreateCommentRequest
  }
): Observable<StrictHttpResponse<Comment>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.CreateInvoiceCommentPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Comment>;
      })
    );
  }

  /**
   * Add a comment to an invoice.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createInvoiceComment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createInvoiceComment(params: {
    id: number;
    context?: HttpContext
    body: CreateCommentRequest
  }
): Observable<Comment> {

    return this.createInvoiceComment$Response(params).pipe(
      map((r: StrictHttpResponse<Comment>) => r.body as Comment)
    );
  }

  /**
   * Path part for operation listClientComments
   */
  static readonly ListClientCommentsPath = '/clients/{id}/comments';

  /**
   * List comments attached to a client.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClientComments()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientComments$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<Comment>>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.ListClientCommentsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Comment>>;
      })
    );
  }

  /**
   * List comments attached to a client.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listClientComments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientComments(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<Comment>> {

    return this.listClientComments$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Comment>>) => r.body as Array<Comment>)
    );
  }

  /**
   * Path part for operation createClientComment
   */
  static readonly CreateClientCommentPath = '/clients/{id}/comments';

  /**
   * Add a comment to a client.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientComment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientComment$Response(params: {
    id: number;
    context?: HttpContext
    body: CreateCommentRequest
  }
): Observable<StrictHttpResponse<Comment>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.CreateClientCommentPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Comment>;
      })
    );
  }

  /**
   * Add a comment to a client.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientComment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientComment(params: {
    id: number;
    context?: HttpContext
    body: CreateCommentRequest
  }
): Observable<Comment> {

    return this.createClientComment$Response(params).pipe(
      map((r: StrictHttpResponse<Comment>) => r.body as Comment)
    );
  }

  /**
   * Path part for operation listEndCustomerComments
   */
  static readonly ListEndCustomerCommentsPath = '/end-customers/{id}/comments';

  /**
   * List comments attached to an end customer.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listEndCustomerComments()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomerComments$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<Comment>>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.ListEndCustomerCommentsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Comment>>;
      })
    );
  }

  /**
   * List comments attached to an end customer.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listEndCustomerComments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomerComments(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<Comment>> {

    return this.listEndCustomerComments$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Comment>>) => r.body as Array<Comment>)
    );
  }

  /**
   * Path part for operation createEndCustomerComment
   */
  static readonly CreateEndCustomerCommentPath = '/end-customers/{id}/comments';

  /**
   * Add a comment to an end customer.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createEndCustomerComment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEndCustomerComment$Response(params: {
    id: number;
    context?: HttpContext
    body: CreateCommentRequest
  }
): Observable<StrictHttpResponse<Comment>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.CreateEndCustomerCommentPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Comment>;
      })
    );
  }

  /**
   * Add a comment to an end customer.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createEndCustomerComment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEndCustomerComment(params: {
    id: number;
    context?: HttpContext
    body: CreateCommentRequest
  }
): Observable<Comment> {

    return this.createEndCustomerComment$Response(params).pipe(
      map((r: StrictHttpResponse<Comment>) => r.body as Comment)
    );
  }

  /**
   * Path part for operation deleteComment
   */
  static readonly DeleteCommentPath = '/comments/{id}';

  /**
   * Soft-delete a comment.
   *
   * Sets `deleted_at` on the comment row. Allowed for the comment's author or users holding the `comments:moderate` permission. Idempotent within the new API; legacy widget reads remain unaffected.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteComment()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteComment$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.DeleteCommentPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Soft-delete a comment.
   *
   * Sets `deleted_at` on the comment row. Allowed for the comment's author or users holding the `comments:moderate` permission. Idempotent within the new API; legacy widget reads remain unaffected.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteComment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteComment(params: {
    id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteComment$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateComment
   */
  static readonly UpdateCommentPath = '/comments/{id}';

  /**
   * Edit body and/or priority of a comment.
   *
   * Allowed for the comment's author or users holding the `comments:moderate` permission. Missing fields are left unchanged.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateComment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateComment$Response(params: {
    id: number;
    context?: HttpContext
    body: UpdateCommentRequest
  }
): Observable<StrictHttpResponse<Comment>> {

    const rb = new RequestBuilder(this.rootUrl, CommentsService.UpdateCommentPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Comment>;
      })
    );
  }

  /**
   * Edit body and/or priority of a comment.
   *
   * Allowed for the comment's author or users holding the `comments:moderate` permission. Missing fields are left unchanged.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateComment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateComment(params: {
    id: number;
    context?: HttpContext
    body: UpdateCommentRequest
  }
): Observable<Comment> {

    return this.updateComment$Response(params).pipe(
      map((r: StrictHttpResponse<Comment>) => r.body as Comment)
    );
  }

}
